import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [downloading, setDownloading] = React.useState(false);
  const { t, i18n } = useTranslation();

  // Detect current language from URL and update i18n
  React.useEffect(() => {
    const lang = location.pathname.includes('/en') ? 'en' : 'ru';
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [location.pathname, i18n]);

  const documents = [
    {
      id: 'privacy',
      title: t('documents.privacyPolicy'),
      subtitle: t('documents.privacySubtitle'),
      fileName: 'policy.pdf',
      displayName: 'policy.pdf',
      description: t('documents.privacyDescription')
    },
    {
      id: 'refund',
      title: t('documents.refundPolicy'),
      subtitle: t('documents.refundSubtitle'),
      fileName: 'refund-policy.pdf',
      displayName: 'refund-policy.pdf',
      description: t('documents.refundDescription')
    },
    {
      id: 'agreement',
      title: t('documents.userAgreement'),
      subtitle: t('documents.agreementSubtitle'),
      fileName: 'user-agreement.pdf',
      displayName: 'user-agreement.pdf',
      description: t('documents.agreementDescription')
    }
  ];

  const [activeDoc, setActiveDoc] = React.useState(documents[0]);

  const handleDownload = async (fileName, displayName) => {
    setDownloading(true);
    try {
      // Создаем временную ссылку для скачивания
      const link = document.createElement('a');
      link.href = `/${fileName}`;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Имитация задержки для UX
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleLanguageSwitch = () => {
    const newLang = i18n.language === 'ru' ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
    if (newLang === 'ru') {
      navigate('/privacy-policy');
    } else {
      navigate('/privacy-policy/en');
    }
  };

  const backToHomePath = i18n.language === 'ru' ? '/' : '/en';

  return (
    <div className="min-h-screen paper-texture" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="border-b-2 border-black tech-lines bg-[#F5F1E8]" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to={backToHomePath} className="inline-flex items-center gap-2 hover:text-[#C41E3A] transition-colors" data-testid="back-home-link">
              <ArrowLeft size={20} />
              {t('documents.backToHome')}
            </Link>
            
            {/* Language Switcher */}
            <button
              onClick={handleLanguageSwitch}
              className="flex items-center gap-2 text-lg hover:text-[#C41E3A] transition-colors"
              aria-label={i18n.language === 'ru' ? t('language.switchTo') : t('language.switchTo')}
            >
              <Globe size={20} />
              <span className="hidden sm:inline">
                {i18n.language === 'ru' ? 'EN' : 'RU'}
              </span>
              <span className="sm:hidden">
                {i18n.language === 'ru' ? t('language.en') : t('language.ru')}
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#C41E3A] rounded-full mb-4">
            <FileText size={32} className="text-white" />
          </div>
          <h1
            className="text-4xl font-bold mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="page-title"
          >
            {t('documents.title')}
          </h1>
          <p className="text-xl text-gray-700" data-testid="page-subtitle">
            {t('documents.subtitle')}
          </p>
        </div>

        {/* Document Selection */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          {documents.map((doc) => (
            <button
              key={doc.id}
              onClick={() => setActiveDoc(doc)}
              className={`engraved-button flex items-center gap-2 ${
                activeDoc.id === doc.id ? 'engraved-button-red' : ''
              }`}
              data-testid={`${doc.id}-tab`}
              aria-label={`View ${doc.title}`}
            >
              <FileText size={16} />
              {doc.title}
            </button>
          ))}
        </div>

        {/* Current Document Info */}
        <div className="text-center mb-6">
          <h2 
            className="text-3xl font-bold mb-2" 
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="active-doc-title"
          >
            {activeDoc.title}
          </h2>
          <p className="text-lg text-gray-700" data-testid="active-doc-subtitle">
            {activeDoc.subtitle}
          </p>
        </div>

        {/* PDF Viewer */}
        <div className="vintage-border bg-[#F5F1E8] p-6 mb-8">
          {/* PDF Container */}
          <div 
            className="w-full h-[600px] border-2 border-black bg-white"
            data-testid="pdf-container"
          >
            <iframe
              src={`/${activeDoc.fileName}`}
              className="w-full h-full"
              title={activeDoc.title}
              data-testid="pdf-iframe"
            >
              <p className="p-4 text-center text-gray-700">
                {t('documents.pdfNotSupported')}
                <br />
                <button
                  onClick={() => handleDownload(activeDoc.fileName, activeDoc.displayName)}
                  className="text-[#C41E3A] hover:underline mt-2"
                >
                  {t('documents.downloadToView')}
                </button>
              </p>
            </iframe>
          </div>

          {/* Download Button */}
          <div className="flex justify-center mt-4">
            <button
              onClick={() => handleDownload(activeDoc.fileName, activeDoc.displayName)}
              className="engraved-button engraved-button-red flex items-center gap-2"
              data-testid="download-button"
              disabled={downloading}
            >
              <Download size={20} />
              {downloading ? t('documents.downloading') : `${t('documents.downloadButton')} ${activeDoc.displayName}`}
            </button>
          </div>
        </div>

        {/* Additional Information */}
        <div className="vintage-border bg-[#E8E3D5] p-6">
          <h3
            className="text-xl font-bold mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="info-title"
          >
            {t('documents.infoTitle')}
          </h3>
          <div className="space-y-4 text-gray-700">
            <p>
              {activeDoc.description}
            </p>
            <p>
              {t('documents.contactQuestions')}
            </p>
            <div className="flex gap-4 mt-6 flex-wrap">
              <Link
                to={i18n.language === 'ru' ? "/contacts" : "/contacts/en"}
                className="engraved-button flex items-center gap-2"
                data-testid="contacts-link"
              >
                {t('documents.contactsLink')}
              </Link>
              <Link
                to={i18n.language === 'ru' ? "/register" : "/register/en"}
                className="engraved-button engraved-button-red flex items-center gap-2"
                data-testid="register-link"
              >
                {t('documents.registerLink')}
              </Link>
            </div>
          </div>
        </div>

        {/* All Documents Download */}
        <div className="mt-8 vintage-border bg-[#F5F1E8] p-6">
          <h3
            className="text-xl font-bold mb-4 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t('documents.downloadAll')}
          </h3>
          <div className="flex flex-wrap gap-4 justify-center">
            {documents.map((doc) => (
              <button
                key={doc.id}
                onClick={() => handleDownload(doc.fileName, doc.displayName)}
                className="engraved-button flex items-center gap-2"
                data-testid={`download-${doc.id}`}
                disabled={downloading}
              >
                <Download size={16} />
                {doc.displayName}
              </button>
            ))}
          </div>
        </div>

        {/* File Info */}
        <div className="mt-6 p-4 bg-[#F5F1E8] border-2 border-dashed border-gray-400 text-center">
          <p className="text-sm text-gray-600" data-testid="file-info">
            {t('documents.fileInfo', { fileName: activeDoc.displayName })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;